(() => {
  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-facet-form]');
      this.status = this.querySelector('[data-facets-status]');
      this.resultsUrl = this.dataset.resultsUrl || this.form?.action || window.location.pathname;
      this.sectionId = this.dataset.sectionId;
      this.requestSequence = 0;
      this.form?.addEventListener('change', this.onChange);
      this.form?.addEventListener('submit', this.onSubmit);
      this.querySelectorAll('[data-active-facet], [data-clear-all]').forEach((link) => link.addEventListener('click', this.onFacetLink));
      this.customClear = this.querySelector('[data-custom-facet-clear]');
      this.customClear?.addEventListener('click', this.clearCustomSize);
      this.querySelectorAll('button[data-grid-density]').forEach((button) => button.addEventListener('click', this.setDensity));
      this.querySelector('[data-facets-toggle]')?.addEventListener('click', this.toggleControls);
      window.addEventListener('popstate', this.onPopState);
      this.restoreDensity();
      this.restoreCustomFilters();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.customClear?.removeEventListener('click', this.clearCustomSize);
      window.removeEventListener('popstate', this.onPopState);
      this.abortController?.abort();
    }

    serializeForm() {
      const params = new URLSearchParams(new FormData(this.form));
      const customSize = this.form?.querySelector('[data-custom-filter="size"]');
      if (customSize) {
        const size = customSize.value || new URL(window.location.href).searchParams.get('filter.v.option.taille') || '';
        if (size) params.set('filter.v.option.taille', size);
        else params.delete('filter.v.option.taille');
      }
      return params.toString();
    }

    onChange = (event) => {
      if (event.target.matches('[data-grid-density]')) return;
      if (event.target.matches('[data-custom-filter="colour"]')) { this.navigateToColour(event.target.value); return; }
      if (event.target.matches('[data-custom-filter="size"]')) { this.applyCustomSize(event.target.value); return; }
      this.renderPage(this.serializeForm());
    };

    onSubmit = (event) => {
      event.preventDefault();
      this.renderPage(this.serializeForm());
    };

    onFacetLink = (event) => { event.preventDefault(); this.removeFacet(event.currentTarget); };

    normalizeFilterValue(value) {
      return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    navigateToColour(value) {
      if (!value) return;
      const destination = new URL(value, window.location.origin);
      const current = new URL(window.location.href);
      const size = current.searchParams.get('filter.v.option.taille');
      const sort = current.searchParams.get('sort_by') || this.form?.querySelector('[name="sort_by"]')?.value;
      if (size) destination.searchParams.set('filter.v.option.taille', size);
      if (sort) destination.searchParams.set('sort_by', sort);
      destination.searchParams.delete('page');
      window.location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
    }

    applyCustomSize(value, { updateHistory = true } = {}) {
      const size = this.normalizeFilterValue(value);
      const url = new URL(window.location.href);
      if (size) url.searchParams.set('filter.v.option.taille', size);
      else url.searchParams.delete('filter.v.option.taille');
      url.searchParams.delete('page');
      if (updateHistory) window.history.pushState({ collection: true }, '', `${url.pathname}${url.search}${url.hash}`);
      const select = this.form?.querySelector('[data-custom-filter="size"]');
      if (select) select.value = size;
      this.updateSizeVisibility(size);
      this.renderCustomFacetState(size);
    }

    restoreCustomFilters() {
      const current = new URL(window.location.href);
      const colour = this.form?.querySelector('[data-custom-filter="colour"]');
      if (colour) {
        const currentPath = current.pathname.replace(/\/$/, '');
        const match = [...colour.options].find((option) => {
          if (!option.value) return false;
          try { return new URL(option.value, window.location.origin).pathname.replace(/\/$/, '') === currentPath; } catch { return false; }
        });
        if (match) colour.value = match.value;
        else colour.selectedIndex = 0;
      }
      const size = this.normalizeFilterValue(current.searchParams.get('filter.v.option.taille'));
      const select = this.form?.querySelector('[data-custom-filter="size"]');
      if (select) select.value = size;
      this.updateSizeVisibility(size);
      this.renderCustomFacetState(size);
    }

    updateSizeVisibility(size) {
      const root = this.closest('[id^="shopify-section-"]') || this.closest('section') || this.parentElement;
      const items = [...(root?.querySelectorAll('[data-product-sizes]') || [])];
      if (!items.length) return;
      let visible = 0;
      items.forEach((item) => {
        const sizes = (item.dataset.productSizes || '').split('|').filter(Boolean);
        const matches = !size || sizes.includes(size);
        item.hidden = !matches;
        if (matches) visible += 1;
      });
      const empty = root?.querySelector('[data-custom-filter-empty]');
      if (empty) empty.hidden = visible > 0;
    }

    renderCustomFacetState(size) {
      if (!this.customClear) return;
      this.customClear.hidden = !size;
      this.customClear.textContent = size ? `${this.dataset.sizeLabel || ''}: ${size.toUpperCase()} ×` : '';
    }

    clearCustomSize = () => { this.applyCustomSize(''); };

    async removeFacet(link) {
      const url = new URL(link.href, window.location.origin);
      const currentSort = new URL(window.location.href).searchParams.get('sort_by') || this.form?.querySelector('[name="sort_by"]')?.value;
      if (currentSort && !url.searchParams.has('sort_by')) url.searchParams.set('sort_by', currentSort);
      await this.renderPage(url.searchParams.toString());
    }

    onPopState = () => this.renderPage(window.location.search.slice(1), { updateHistory: false });

    async renderPage(search, { updateHistory = true } = {}) {
      const query = new URLSearchParams(search);
      const historyQuery = query.toString();
      query.set('section_id', this.sectionId);
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      const requestSequence = ++this.requestSequence;
      this.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = this.dataset.loadingText || '';
      try {
        const response = await fetch(`${this.resultsUrl}?${query.toString()}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const html = await response.text();
        if (requestSequence !== this.requestSequence || controller.signal.aborted) return;
        const documentResult = new DOMParser().parseFromString(html, 'text/html');
        const replacement = documentResult.querySelector(`#shopify-section-${this.sectionId}`) || documentResult.querySelector('[data-product-grid-container]')?.closest('section');
        const current = this.closest(`#shopify-section-${this.sectionId}`) || this.parentElement;
        if (!replacement || !current) throw new Error('Collection section missing');
        if (requestSequence !== this.requestSequence || controller.signal.aborted) return;
        current.innerHTML = replacement.innerHTML;
        const renderedFacets = current.querySelector('facet-filters-form');
        renderedFacets?.restoreCustomFilters();
        renderedFacets?.announceResults();
        if (updateHistory) window.history.pushState({ collection: true }, '', `${this.resultsUrl}${historyQuery ? `?${historyQuery}` : ''}`);
      } catch {
        if (requestSequence !== this.requestSequence || controller.signal.aborted) return;
        this.announceError();
      } finally { this.removeAttribute('aria-busy'); }
    }

    announceResults() {
      const root = this.closest('[id^="shopify-section-"]') || this.closest('section') || this.parentElement;
      const countElement = root?.querySelector('[data-grid-result-count]');
      const count = Number(countElement?.dataset.count);
      if (!this.status || !Number.isFinite(count)) return;
      const template = count === 1 ? this.dataset.resultsCountOne : this.dataset.resultsCountOther;
      this.status.textContent = (template || '').replace('{{ count }}', String(count));
    }

    announceError() {
      if (this.status) this.status.textContent = this.dataset.resultsError || '';
      const apply = this.querySelector('[data-facet-apply]');
      if (apply) apply.textContent = this.dataset.retryText || apply.textContent;
    }

    setDensity = (event) => {
      const density = event.currentTarget.dataset.gridDensity;
      this.applyDensity(density);
      try { window.localStorage.setItem('maison-la-muse-grid-density', density); } catch { /* Optional preference. */ }
    };

    restoreDensity() {
      try { this.applyDensity(window.localStorage.getItem('maison-la-muse-grid-density') || 'four'); } catch { this.applyDensity('four'); }
    }

    applyDensity(density) {
      if (!['three', 'four', 'five'].includes(density)) density = 'four';
      const root = this.closest('[id^="shopify-section-"]') || this.closest('section') || this.parentElement;
      const grid = root?.querySelector('[data-product-grid]');
      if (grid) grid.dataset.gridDensity = density;
      this.querySelectorAll('button[data-grid-density]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.gridDensity === density)));
    }

    toggleControls = () => {
      const controls = this.querySelector('[data-facet-controls]');
      const trigger = this.querySelector('[data-facets-toggle]');
      if (!controls || !trigger) return;
      controls.toggleAttribute('data-mobile-open');
      trigger.setAttribute('aria-expanded', String(controls.hasAttribute('data-mobile-open')));
    };
  }
  if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
})();
